<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../js/api.js';
  import { auth } from '../js/auth.js';
  import { toast } from '../js/toast.js';
  import { spinner } from '../js/spinner.js';
  import { push } from 'svelte-spa-router';
  import SubscribeToPlanModal from './SubscribeToPlanModal.svelte';
  import ConfirmDialog from '../../components/ConfirmDialog.svelte';
  import { StatusBadge } from './app';
  import { APP_CURRENCY, formatMoney } from '../../js/currency.js';
  import { isPlanDowngrade, isPlanUpgrade } from '../../js/planTier.js';

  let plans: any[] = [];
  let selectedPlan: any = null;
  let showSubscribeModal = false;
  let isProcessing = false;
  let isLoading = true;
  let billingInterval: 'monthly' | 'annual' = 'monthly';
  let isOnTrial = false;
  let teamData: any = null;
  let isTeamOwner = false;
  let showCancelConfirm = false;
  let isCancelling = false;
  let currencyCode = APP_CURRENCY;

  onMount(async () => {
    await loadData();
  });

  async function loadData() {
    isLoading = true;
    spinner.show();
    try {
      const teamUuid = $auth.user?.active_team_uuid;
      const promises: Promise<any>[] = [
        api.getPaymentPlans() as Promise<any>,
      ];
      if (teamUuid) {
        promises.push(api.getTeam(teamUuid) as Promise<any>);
        promises.push(api.getTeamMembers(teamUuid) as Promise<any>);
      }

      const [plansResponse, teamResponse, membersResponse] = await Promise.all(promises);

      if (plansResponse.success) {
        plans = plansResponse.data.plans || [];
        if (plansResponse.data.currency) {
          currencyCode = String(plansResponse.data.currency).toUpperCase();
        }
      } else {
        toast.showNotification(false, plansResponse.message || 'Failed to load plans');
      }

      if (teamResponse?.success) {
        teamData = teamResponse.data?.team || null;
        isOnTrial = teamData?.is_on_trial || false;
        if (teamData?.billing_interval === 'annual' || teamData?.billing_interval === 'monthly') {
          billingInterval = teamData.billing_interval;
        }
      }

      if (membersResponse?.success) {
        const members = membersResponse.data?.members || [];
        const currentUserMember = members.find((m: any) => m.user_uuid === $auth.user?.uuid);
        isTeamOwner = currentUserMember?.role === 'owner';
      }
    } catch (error) {
      toast.showNotification(false, 'Error loading payment plans');
      console.error(error);
    } finally {
      spinner.hide();
      isLoading = false;
    }
  }

  function selectPlan(plan: any) {
    selectedPlan = plan;
    showSubscribeModal = true;
  }

  function handleSubscribed() {
    showSubscribeModal = false;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('billing-updated'));
    }
    push('/payment-plan');
  }

  function formatPrice(price: number): string {
    return formatMoney(Number(price) || 0, currencyCode);
  }

  function formatPeriodDate(dateString: string | null | undefined): string | null {
    if (!dateString) return null;
    const date = new Date(dateString.includes('T') ? dateString : `${dateString}T12:00:00`);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(navigator.language, { dateStyle: 'long' });
  }

  function isCurrentPlan(plan: any): boolean {
    return teamData?.payment_plan?.uuid === plan.uuid;
  }

  function getDisplayPrice(plan: any, interval: string): string {
    if (plan.included_briefs === 0) {
      return formatPrice(plan.price);
    }
    if (interval === 'annual' && plan.annual_price) {
      return formatPrice(Math.round(parseFloat(plan.annual_price) / 12));
    }
    return formatPrice(plan.price);
  }

  function getPriceSuffix(plan: any): string {
    if (plan.included_briefs === 0) {
      return '/brief';
    }
    return '/month';
  }

  function getAnnualSavings(plan: any, interval: string): number | null {
    if (interval !== 'annual' || !plan.annual_price || plan.included_briefs === 0) return null;
    const monthlyCost = Number(plan.price) * 12;
    const savings = monthlyCost - Number(plan.annual_price);
    return savings > 0 ? savings : null;
  }

  function formatAnnualSavings(plan: any, interval: string): string | null {
    const savings = getAnnualSavings(plan, interval);
    return savings != null ? formatPrice(savings) : null;
  }

  $: effectivePlan = teamData?.payment_plan || null;
  $: teamName = teamData?.name?.trim() || '';

  function getRoleLabel(): string {
    return isTeamOwner ? 'Owner' : 'Member';
  }

  function getRoleTone(): 'info' | 'neutral' {
    return isTeamOwner ? 'info' : 'neutral';
  }

  function getButtonText(plan: any): string {
    if (isCurrentPlan(plan)) {
      return 'Current Plan';
    }
    if (effectivePlan) {
      if (isPlanDowngrade(plan, effectivePlan)) {
        return 'Downgrade';
      }
      if (isPlanUpgrade(plan, effectivePlan)) {
        return 'Upgrade';
      }
      return 'Switch plan';
    }
    return 'Start 14-Day Free Trial';
  }

  function isPlanHighlighted(plan: any): boolean {
    return plan.name === 'Professional';
  }

  $: periodEndDate = (() => {
    if (!teamData || isOnTrial) return null;
    return teamData.renewal_date || teamData.trial_ends_at || null;
  })();

  $: periodEndLabel = formatPeriodDate(periodEndDate);

  $: billingIntervalLabel = teamData?.billing_interval === 'annual'
    ? 'annual billing'
    : teamData?.billing_interval === 'monthly'
      ? 'monthly billing'
      : null;

  $: cancellationLabel = formatPeriodDate(teamData?.cancellation_date);

  async function handleCancelAccount() {
    const teamUuid = $auth.user?.active_team_uuid;
    if (!teamUuid || !isTeamOwner || isCancelling) return;

    isCancelling = true;
    try {
      const res = (await api.cancelTeamPlan(teamUuid)) as any;
      toast.showNotification(!!res?.success, res?.message || 'Unable to cancel account.');
      if (res?.success) {
        showCancelConfirm = false;
        await loadData();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('billing-updated'));
        }
      }
    } catch (err) {
      console.error('Failed to cancel account', err);
      toast.showNotification(false, 'Failed to cancel account.');
    } finally {
      isCancelling = false;
    }
  }
</script>

<div class="payment-plan-selection">
  <div class="page-header">
    <button class="back-link" on:click={() => push('/payment-plan')}>
      <i class="arrow left icon"></i>
      Back to My Plan
    </button>
    <h2 class="page-title">{effectivePlan ? 'Change Your Plan' : 'Choose Your Plan'}</h2>

    {#if teamName}
      <div class="team-context" aria-label="Billing applies to team {teamName}">
        <div class="team-context-leading" aria-hidden="true">
          <i class="users icon"></i>
        </div>
        <div class="team-context-body">
          <span class="team-context-eyebrow">Team</span>
          <span class="team-context-name">{teamName}</span>
          <p class="team-context-note">Plan changes, billing, and cancellation apply to this team.</p>
        </div>
        <StatusBadge label={getRoleLabel()} tone={getRoleTone()} />
      </div>
    {/if}

    <p class="page-subtitle">
      {#if effectivePlan && teamName}
        <strong>{teamName}</strong> is on the <strong>{effectivePlan.name}</strong> plan{isOnTrial ? ' (trial)' : ''}.
      {:else if effectivePlan}
        You're currently on the <strong>{effectivePlan.name}</strong> plan{isOnTrial ? ' (trial)' : ''}.
      {:else if teamName}
        Choose a plan for <strong>{teamName}</strong>. Start with a 14-day free trial — no credit card required.
      {:else}
        Start with a 14-day free trial. No credit card required.
      {/if}
    </p>

    {#if isOnTrial && teamData?.trial_ends_at}
      <p class="period-end-note">
        Your free trial ends on <strong>{formatPeriodDate(teamData.trial_ends_at)}</strong>.
      </p>
    {:else if effectivePlan && periodEndLabel}
      <p class="period-end-note">
        Your current plan period ends on <strong>{periodEndLabel}</strong>{#if billingIntervalLabel}
          <span class="period-interval">({billingIntervalLabel})</span>{/if}.
      </p>
    {/if}

    {#if cancellationLabel}
      <p class="cancellation-note">
        Account cancellation is scheduled for <strong>{cancellationLabel}</strong>. You keep access until then.
      </p>
    {/if}

    <!-- Billing Toggle -->
    <div class="billing-toggle">
      <button
        class="toggle-option"
        class:active={billingInterval === 'monthly'}
        on:click={() => billingInterval = 'monthly'}
      >
        Monthly
      </button>
      <button
        class="toggle-option"
        class:active={billingInterval === 'annual'}
        on:click={() => billingInterval = 'annual'}
      >
        Annual
        <span class="save-badge">Save up to 10%</span>
      </button>
    </div>
  </div>

  {#if isLoading}
    <div class="loading-state">
      <i class="spinner loading icon"></i>
      Loading plans...
    </div>
  {:else}
    <div class="plans-grid">
      {#each plans as plan (plan.uuid)}
        <div class="plan-card" class:highlighted={isPlanHighlighted(plan)} class:current={isCurrentPlan(plan)}>
          {#if isCurrentPlan(plan)}
            <div class="current-plan-ribbon">Current Plan</div>
          {/if}
          {#if isPlanHighlighted(plan) && !isCurrentPlan(plan)}
            <div class="popular-badge">Most Popular</div>
          {/if}

          <div class="plan-header">
            <h3 class="plan-name">{plan.name}</h3>
            {#if plan.description}
              <p class="plan-description">{plan.description}</p>
            {/if}
          </div>

          <div class="plan-price">
            <span class="price-amount">{getDisplayPrice(plan, billingInterval)}</span>
            <span class="price-period">{getPriceSuffix(plan)}</span>
          </div>

          {#if billingInterval === 'annual' && plan.annual_price}
            <div class="annual-billing-box">
              <span class="annual-total">{formatPrice(plan.annual_price)}/year</span>
              {#if formatAnnualSavings(plan, billingInterval)}
                <span class="annual-saving">You save {formatAnnualSavings(plan, billingInterval)}</span>
              {/if}
            </div>
          {/if}

          {#if plan.included_briefs > 0}
            <div class="briefs-included">
              <strong>{plan.included_briefs}</strong> briefs included per month
            </div>
          {/if}

          {#if plan.included_briefs === 0 && plan.extra_brief_price}
            <div class="briefs-included pack-note">
              3 briefs for {formatPrice(129)}, save $18
            </div>
          {/if}

          <button
            class="plan-select-button"
            class:current={isCurrentPlan(plan)}
            class:highlighted={isPlanHighlighted(plan) && !isCurrentPlan(plan)}
            on:click={() => selectPlan(plan)}
            disabled={isProcessing || isCurrentPlan(plan)}
          >
            {getButtonText(plan)}
          </button>

          {#if plan.features && plan.features.length > 0}
            <div class="plan-features">
              <div class="features-divider"></div>
              {#each plan.features as feature}
                <div class="feature-item">
                  <i class="check icon feature-icon"></i>
                  <span>{feature}</span>
                </div>
              {/each}
            </div>
          {/if}

          {#if plan.extra_brief_price && plan.included_briefs > 0}
            <div class="extra-brief-note">
              Extra briefs: {formatPrice(plan.extra_brief_price)} each
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}

  {#if effectivePlan && !isLoading}
    <section class="cancel-section" aria-labelledby="cancel-account-heading">
      <h3 id="cancel-account-heading" class="cancel-heading">Cancel account</h3>
      {#if cancellationLabel}
        <p class="cancel-copy">
          <strong>{teamName}</strong> is scheduled to cancel on <strong>{cancellationLabel}</strong>.
          Access continues until that date.
        </p>
      {:else if isTeamOwner}
        <p class="cancel-copy">
          Cancel <strong>{teamName}</strong> at the end of the current billing period
          {#if periodEndLabel}
            (<strong>{periodEndLabel}</strong>)
          {/if}.
          This team will lose plan access after that date.
        </p>
        <button
          type="button"
          class="cancel-account-button"
          on:click={() => (showCancelConfirm = true)}
          disabled={isCancelling}
        >
          Cancel account
        </button>
      {:else}
        <p class="cancel-copy cancel-owner-only">
          Only the team owner can cancel this account.
        </p>
      {/if}
    </section>
  {/if}
</div>

<SubscribeToPlanModal
  bind:showModal={showSubscribeModal}
  plan={selectedPlan}
  currentPlan={effectivePlan}
  {isOnTrial}
  {billingInterval}
  {currencyCode}
  on:subscribed={handleSubscribed}
/>

<ConfirmDialog
  bind:show={showCancelConfirm}
  title="Cancel account?"
  message={periodEndLabel
    ? `${teamName ? `${teamName} will ` : 'Your team will '}remain active until ${periodEndLabel}, then the plan will end.`
    : `${teamName ? `This will cancel the plan for ${teamName}` : 'Your team plan will be cancelled'} at the end of the current billing period.`}
  confirmText={isCancelling ? 'Cancelling…' : 'Yes, cancel account'}
  cancelText="Keep account"
  type="danger"
  icon="warning sign"
  on:confirm={handleCancelAccount}
/>

<style>
  .payment-plan-selection {
    padding: 0;
  }

  .page-header {
    margin-bottom: 2rem;
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    color: var(--primary);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    padding: 0;
    margin-bottom: 1rem;
    transition: color 0.2s;
  }

  .back-link:hover {
    color: var(--darkprimary);
  }

  .back-link i {
    margin: 0;
  }

  .page-title {
    font-size: 2rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 0.75rem 0;
  }

  .team-context {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-4);
    margin-bottom: var(--space-4);
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-card);
  }

  .team-context-leading {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    flex-shrink: 0;
    border-radius: var(--radius-card);
    background: var(--status-info-soft);
    color: var(--primary);
  }

  .team-context-leading .icon {
    margin: 0;
    font-size: 1.1rem;
  }

  .team-context-body {
    flex: 1;
    min-width: 0;
  }

  .team-context-eyebrow {
    display: block;
    font-size: var(--text-xs);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--grey-secondary);
    line-height: 1.2;
    margin-bottom: 2px;
  }

  .team-context-name {
    display: block;
    font-size: 1.125rem;
    font-weight: 500;
    color: var(--grey);
    line-height: 1.3;
    margin-bottom: var(--space-1);
  }

  .team-context-note {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    line-height: 1.45;
  }

  .page-subtitle {
    color: var(--grey-secondary);
    font-size: 1rem;
    margin: 0 0 1.5rem 0;
  }

  .page-subtitle strong {
    color: var(--grey);
  }

  .period-end-note,
  .cancellation-note {
    color: var(--grey-secondary);
    font-size: 0.95rem;
    margin: 0 0 0.75rem 0;
    line-height: 1.5;
  }

  .period-end-note strong,
  .cancellation-note strong {
    color: var(--grey);
    font-weight: 500;
  }

  .period-interval {
    color: var(--grey-secondary);
  }

  .cancellation-note {
    color: var(--status-warn-ink, var(--grey));
    background: var(--status-warn-soft, var(--greyish));
    border-radius: var(--radius-card, 8px);
    padding: var(--space-3) var(--space-4);
  }

  /* Billing Toggle */
  .billing-toggle {
    display: inline-flex;
    background: var(--greyish, #f0f0f0);
    border-radius: 10px;
    padding: 4px;
    gap: 2px;
  }

  .toggle-option {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 1.25rem;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--grey-secondary);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .toggle-option.active {
    background: var(--white);
    color: var(--grey);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .save-badge {
    font-size: 0.7rem;
    background: #dcfce7;
    color: #16a34a;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
  }

  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 4rem 2rem;
    color: var(--grey-secondary);
  }

  /* Plans Grid */
  .plans-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-top: 1.5rem;
  }

  .plan-card {
    background: var(--white);
    border: 2px solid var(--border-color, #e5e7eb);
    border-radius: 16px;
    padding: 2rem 1.5rem;
    display: flex;
    flex-direction: column;
    transition: transform 0.2s, box-shadow 0.2s;
    position: relative;
    overflow: hidden;
  }

  .plan-card:hover:not(.current) {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  .plan-card.highlighted {
    border-color: var(--primary);
    box-shadow: 0 4px 12px rgba(5, 108, 242, 0.15);
  }

  .plan-card.current {
    border-color: #45c496;
    background: linear-gradient(to bottom, #f0fdf4, var(--white));
  }

  .current-plan-ribbon {
    position: absolute;
    top: 12px;
    right: -32px;
    background: #45c496;
    color: var(--white);
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.25rem 2.5rem;
    transform: rotate(45deg);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .popular-badge {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    background: var(--primary);
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.3rem 1rem;
    border-radius: 0 0 8px 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .plan-header {
    margin-bottom: 1rem;
  }

  .plan-name {
    font-size: 1.35rem;
    font-weight: 600;
    color: var(--grey);
    margin: 0 0 0.35rem 0;
  }

  .plan-description {
    font-size: 0.85rem;
    color: var(--grey-secondary);
    margin: 0;
    line-height: 1.4;
  }

  /* Price */
  .plan-price {
    margin-bottom: 0.25rem;
    display: flex;
    align-items: baseline;
  }

  .price-amount {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--grey);
    letter-spacing: -1px;
  }

  .price-period {
    font-size: 0.95rem;
    color: var(--grey-secondary);
    margin-left: 0.25rem;
  }

  .annual-billing-box {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.75rem;
  }

  .annual-total {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--grey);
  }

  .annual-saving {
    font-size: 0.75rem;
    font-weight: 600;
    color: #16a34a;
    background: #dcfce7;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .briefs-included {
    font-size: 0.9rem;
    color: var(--grey);
    margin-bottom: 1.25rem;
    padding: 0.5rem 0.75rem;
    background: var(--greyish, #f8f9fa);
    border-radius: 8px;
  }

  .briefs-included.pack-note {
    color: var(--grey-secondary);
    font-size: 0.85rem;
  }

  /* Button */
  .plan-select-button {
    width: 100%;
    padding: 0.85rem 1.5rem;
    border: 2px solid var(--primary);
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s;
    background: transparent;
    color: var(--primary);
    margin-bottom: 1.25rem;
  }

  .plan-select-button:hover:not(:disabled):not(.current) {
    background: var(--primary);
    color: white;
  }

  .plan-select-button.highlighted {
    background: var(--primary);
    color: white;
  }

  .plan-select-button.highlighted:hover:not(:disabled) {
    background: var(--darkprimary);
    border-color: var(--darkprimary);
  }

  .plan-select-button.current {
    background: var(--greyish);
    color: var(--grey-secondary);
    border-color: var(--greyish);
    cursor: default;
  }

  .plan-select-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .plan-select-button:active:not(:disabled):not(.current) {
    transform: translateY(1px);
  }

  /* Features */
  .plan-features {
    flex: 1;
  }

  .features-divider {
    height: 1px;
    background: var(--border-color, #e5e7eb);
    margin-bottom: 1rem;
  }

  .feature-item {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    margin-bottom: 0.65rem;
    color: var(--grey);
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .feature-icon {
    color: #45c496;
    font-size: 0.8rem !important;
    margin: 0 !important;
    flex-shrink: 0;
    margin-top: 2px !important;
  }

  .extra-brief-note {
    margin-top: 0.75rem;
    font-size: 0.8rem;
    color: var(--grey-secondary);
    padding-top: 0.75rem;
    border-top: 1px solid var(--border-color, #e5e7eb);
  }

  .cancel-section {
    margin-top: var(--space-8, 2rem);
    padding-top: var(--space-6, 1.5rem);
    border-top: 1px solid var(--border-color);
    max-width: 40rem;
  }

  .cancel-heading {
    font-size: 1.1rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 var(--space-3, 0.75rem);
  }

  .cancel-copy {
    color: var(--grey-secondary);
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0 0 var(--space-4, 1rem);
  }

  .cancel-copy strong {
    color: var(--grey);
    font-weight: 500;
  }

  .cancel-owner-only {
    margin-bottom: 0;
  }

  .cancel-account-button {
    border: 1px solid var(--status-danger, #d91b42);
    background: transparent;
    color: var(--status-danger-ink, var(--status-danger, #d91b42));
    border-radius: var(--radius-button, 6px);
    padding: var(--space-2, 0.5rem) var(--space-4, 1rem);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
  }

  .cancel-account-button:hover:not(:disabled) {
    background: var(--status-danger-soft);
  }

  .cancel-account-button:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .cancel-account-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    .plans-grid {
      grid-template-columns: 1fr;
    }

    .billing-toggle {
      display: flex;
    }

    .toggle-option {
      flex: 1;
      justify-content: center;
    }
  }
</style>
